// src/features/TechnicianAnalysis/RecurringIssuesPage.js
import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Grid, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, Stack, LinearProgress, Alert, Button
} from '@mui/material';
import {
  Warning,
  TrendingUp,
  Build,
  Refresh
} from '@mui/icons-material';
import { useI18n } from '../../i18n/i18n';
import { getData } from '../../services/storageService';

const RecurringIssuesPage = ({ user }) => {
  const { t } = useI18n();
  const [issues, setIssues] = useState([]);
  const [trends, setTrends] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadRecurringIssues();
  }, []);

  const loadRecurringIssues = async () => {
    try {
      const maintenance = await getData('maintenance') || [];
      const incidents = await getData('incidents') || [];
      
      // Додаємо демо-дані, якщо немає реальних
      const demoMaintenance = maintenance.length === 0 ? generateDemoMaintenance() : maintenance;
      const demoIncidents = incidents.length === 0 ? generateDemoIncidents() : incidents;
      
      // Analyze recurring issues from maintenance
      const maintenanceIssues = analyzeMaintenanceIssues(demoMaintenance);
      
      // Analyze recurring issues from incidents
      const incidentIssues = analyzeIncidentIssues(demoIncidents);
      
      // Combine and sort by frequency
      const allIssues = [...maintenanceIssues, ...incidentIssues]
        .sort((a, b) => b.frequency - a.frequency)
        .slice(0, 10);

      setIssues(allIssues);
      setTrends(analyzeTrends(demoMaintenance, demoIncidents));
      setLoading(false);
    } catch (error) {
      console.error('Error loading recurring issues:', error);
      // Завантажуємо демо-дані у випадку помилки
      setIssues(generateDemoIssues());
      setTrends(generateDemoTrends());
      setLoading(false);
    }
  };

  const generateDemoMaintenance = () => {
    return [
      {
        id: 'm1',
        machineId: 'L-12',
        machineName: 'Lavadora L-12',
        type: 'repair',
        description: 'Fuga de agua en válvula principal',
        date: '2024-01-15',
        priority: 'high'
      },
      {
        id: 'm2',
        machineId: 'L-12', 
        machineName: 'Lavadora L-12',
        type: 'repair',
        description: 'Problema con bomba de agua',
        date: '2024-01-10',
        priority: 'high'
      },
      {
        id: 'm3',
        machineId: 'S-05',
        machineName: 'Secadora S-05',
        type: 'cleaning',
        description: 'Limpieza de filtros',
        date: '2024-01-12',
        priority: 'medium'
      }
    ];
  };

  const generateDemoIncidents = () => {
    return [
      {
        id: 'i1',
        type: 'technical',
        client: 'Hotel Plaza',
        description: 'Problema con suministro de agua caliente',
        date: '2024-01-14',
        priority: 'high'
      },
      {
        id: 'i2',
        type: 'technical',
        client: 'Hotel Plaza',
        description: 'Fallo en sistema de calefacción',
        date: '2024-01-08',
        priority: 'high'
      }
    ];
  };

  const generateDemoIssues = () => {
    return [
      {
        id: 'L-12-repair',
        type: 'maintenance',
        machine: 'Lavadora L-12',
        issueType: 'repair',
        description: 'Problemas recurrentes con sistema de agua',
        frequency: 3,
        lastOccurrence: '2024-01-15',
        priority: 'high'
      },
      {
        id: 'Hotel Plaza-technical',
        type: 'incident', 
        client: 'Hotel Plaza',
        issueType: 'technical',
        description: 'Problemas con suministro de agua caliente',
        frequency: 2,
        lastOccurrence: '2024-01-14',
        priority: 'high'
      }
    ];
  };

  const generateDemoTrends = () => {
    return [
      { month: '2024-01', count: 5 },
      { month: '2023-12', count: 3 },
      { month: '2023-11', count: 4 },
      { month: '2023-10', count: 2 },
      { month: '2023-09', count: 6 },
      { month: '2023-08', count: 4 }
    ];
  };

  // Інші функції залишаються незмінними...
  const analyzeMaintenanceIssues = (maintenance) => {
    const issueMap = {};
    
    maintenance.forEach(task => {
      const key = `${task.machineId}-${task.type}`;
      if (!issueMap[key]) {
        issueMap[key] = {
          id: key,
          type: 'maintenance',
          machine: task.machineName || task.machineId,
          issueType: task.type,
          description: task.description,
          frequency: 0,
          lastOccurrence: task.date,
          priority: task.priority || 'medium'
        };
      }
      issueMap[key].frequency++;
      if (new Date(task.date) > new Date(issueMap[key].lastOccurrence)) {
        issueMap[key].lastOccurrence = task.date;
      }
    });

    return Object.values(issueMap).filter(issue => issue.frequency > 1);
  };

  const analyzeIncidentIssues = (incidents) => {
    const issueMap = {};
    
    incidents.forEach(incident => {
      const key = `${incident.type}-${incident.client}`;
      if (!issueMap[key]) {
        issueMap[key] = {
          id: key,
          type: 'incident',
          client: incident.client,
          issueType: incident.type,
          description: incident.description,
          frequency: 0,
          lastOccurrence: incident.date,
          priority: incident.priority || 'medium'
        };
      }
      issueMap[key].frequency++;
      if (new Date(incident.date) > new Date(issueMap[key].lastOccurrence)) {
        issueMap[key].lastOccurrence = incident.date;
      }
    });

    return Object.values(issueMap).filter(issue => issue.frequency > 1);
  };

  const analyzeTrends = (maintenance, incidents) => {
    const monthlyData = {};
    
    [...maintenance, ...incidents].forEach(item => {
      const month = item.date?.substring(0, 7); // YYYY-MM
      if (month) {
        if (!monthlyData[month]) {
          monthlyData[month] = 0;
        }
        monthlyData[month]++;
      }
    });

    return Object.entries(monthlyData)
      .map(([month, count]) => ({ month, count }))
      .sort((a, b) => a.month.localeCompare(b.month))
      .slice(-6);
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'info';
      default: return 'default';
    }
  };

  const getIssueTypeColor = (type) => {
    switch (type) {
      case 'repair': return 'error';
      case 'planned': return 'success';
      case 'cleaning': return 'info';
      case 'diagnostics': return 'warning';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Typography>{t('common_loading')}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        {t('recurring_issues_title')}
      </Typography>
      <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
        {t('recurring_issues_subtitle')}
      </Typography>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ textAlign: 'center', p: 2 }}>
            <Typography variant="h4" color="warning.main" fontWeight="bold">
              {issues.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('recurring_issues_total')}
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ textAlign: 'center', p: 2 }}>
            <Typography variant="h4" color="error.main" fontWeight="bold">
              {issues.filter(i => i.priority === 'high').length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('recurring_issues_critical')}
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ textAlign: 'center', p: 2 }}>
            <Typography variant="h4" color="info.main" fontWeight="bold">
              {trends[trends.length - 1]?.count || 0}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('recurring_issues_current_month')}
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ textAlign: 'center', p: 2 }}>
            <Typography variant="h4" color="success.main" fontWeight="bold">
              {Math.round((issues.filter(i => i.priority !== 'high').length / Math.max(issues.length, 1)) * 100) || 0}%
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('recurring_issues_resolved')}
            </Typography>
          </Card>
        </Grid>
      </Grid>

      {/* Recurring Issues Table */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
            <Typography variant="h6">
              {t('recurring_issues_list')}
            </Typography>
            <Button startIcon={<Refresh />} onClick={loadRecurringIssues}>
              {t('common_refresh')}
            </Button>
          </Stack>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>{t('recurring_issues_machine')}</strong></TableCell>
                  <TableCell><strong>{t('recurring_issues_type')}</strong></TableCell>
                  <TableCell><strong>{t('recurring_issues_frequency')}</strong></TableCell>
                  <TableCell><strong>{t('recurring_issues_priority')}</strong></TableCell>
                  <TableCell><strong>{t('recurring_issues_last_occurrence')}</strong></TableCell>
                  <TableCell><strong>{t('recurring_issues_trend')}</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {issues.map((issue, index) => (
                  <TableRow key={issue.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {issue.machine || issue.client}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {issue.description?.substring(0, 50)}...
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={t(`maintlog_event_type_${issue.issueType}`) || issue.issueType}
                        color={getIssueTypeColor(issue.issueType)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2">{issue.frequency}</Typography>
                        <LinearProgress 
                          variant="determinate" 
                          value={Math.min(issue.frequency * 20, 100)}
                          color={issue.frequency > 3 ? 'error' : issue.frequency > 2 ? 'warning' : 'info'}
                          sx={{ width: 60, height: 8, borderRadius: 4 }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={t(`ai_priority_${issue.priority}`)}
                        color={getPriorityColor(issue.priority)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(issue.lastOccurrence).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <TrendingUp sx={{ 
                        color: issue.frequency > 3 ? 'error.main' : 'success.main',
                        transform: issue.frequency > 3 ? 'rotate(0deg)' : 'rotate(180deg)'
                      }} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {issues.length === 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              {t('recurring_issues_none')}
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Trends Chart */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('recurring_issues_trends')}
          </Typography>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {trends.map((trend, index) => (
              <Grid item xs={2} key={trend.month}>
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="caption" display="block">
                    {trend.month}
                  </Typography>
                  <Typography variant="h6" fontWeight="bold">
                    {trend.count}
                  </Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={Math.min((trend.count / Math.max(...trends.map(t => t.count))) * 100, 100)}
                    color={index === trends.length - 1 ? 'error' : 'primary'}
                    sx={{ mt: 1, height: 6, borderRadius: 3 }}
                  />
                </Paper>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default RecurringIssuesPage;